import React from "react";
import { Heading } from "@/shared/components/ui/Heading";
import FullCoverageDetailsButton from "./FullCoverageDetailsButton";
import CoverageIconCategories from "./CoverageIconCategories";
import Strings from "../utils/Strings.constants";
import { useModal } from "@/shared/contexts/ModalProvider";
import FullCoverageDetailsContent from "@/shared/components/FullCoverageDetailsModal";
import { CoverageSettings, Quote } from "@/shared/types/Quote.interface";
import { PetUnderwriterType } from "@spotpetinsurance/spot-types/entities/PetQuote";
import { useFormParentContext } from "@/shared/contexts/FormParent";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { useDeepCompareMemo } from "@react-hookz/web";
import { Species } from "@spotpetinsurance/spot-types/entities/Species";
import { BreedConditionData } from "../hooks/useHasBreedConditions";

interface CoverageDisplayConfig {
    samplePolicyUrl: string;
    showTelehealth?: boolean;
    isDentalCovered?: boolean;
    isMicrochippingCovered?: boolean;
    preExistingConditions?: string;
    showCompellingStats?: boolean;
    showBreedSpecificConditions?: boolean;
    hasAnyBreedConditions?: boolean;
    breedConditionData?: BreedConditionData | null;
    showNotCovered?: boolean;
    onSwitchToAccidentAndIllness?: () => void;
}

interface CoverageIconGridProps {
    coverageInfo?: CoverageSettings;
    species?: Species;
    underwriter: PetUnderwriterType;
    displayConfig: CoverageDisplayConfig;
    showDetailsCtaGravitas?: boolean;
}

export function CoverageIconGrid(props: CoverageIconGridProps) {
    const { coverageInfo, displayConfig, showDetailsCtaGravitas } = props;
    const { showModal } = useModal();

    const { watch } = useFormParentContext<Quote>();
    const policies = watch("policies");
    const { appState } = useAppLayerContext();
    const activePet = useDeepCompareMemo(() => policies?.[appState.activePetIndex || 0], [policies, appState.activePetIndex]);

    const handleOpenModal = React.useCallback(() => {
        showModal(
            "coverage-details-modal",
            <FullCoverageDetailsContent {...displayConfig} activePet={activePet} policies={policies} isMultiPetLinked={appState.isMultiPetLinked} />,
            Strings.COVERAGE_DETAILS
        );
    }, [showModal, displayConfig, activePet, policies, appState.isMultiPetLinked]);

    if (!coverageInfo) return null;

    return (
        <div className="flex flex-col items-center gap-4">
            <div className="hidden w-full flex-row items-center justify-between px-4 lg:flex lg:px-0">
                <Heading level="h2" className="text-sm font-semibold lg:text-base lg:font-bold">
                    {Strings.ELIGIBLE_CLAIMS_HEADER}
                </Heading>
                <FullCoverageDetailsButton onClick={handleOpenModal} showDetailsCtaGravitas={showDetailsCtaGravitas} />
            </div>
            <CoverageIconCategories
                hasAnyBreedConditions={displayConfig.hasAnyBreedConditions}
                breedConditionData={displayConfig.breedConditionData}
                policies={policies}
                // show not covered test
                showNotCovered={displayConfig.showNotCovered}
                onSwitchToAccidentAndIllness={displayConfig.onSwitchToAccidentAndIllness}
                // test end
                {...props}
            />
        </div>
    );
}
